import { api } from '@/lib/api/requests'
import type { ExcusedReturn } from './types'

// The viewer's personal lesson cabinet — their own enrollments plus those of any
// guardian-managed children (dependents), across every club. Mirrors the backend
// /me/lessons contract (backend commit 180d8a47): two pre-sorted buckets,
// `upcoming` ascending and `past` descending. No linked player → empty arrays.

export type LessonEnrolleeType = 'player' | 'dependent'

// `active`   — confirmed seat.
// `waitlisted` — on the waitlist (kept quiet in the UI).
// `cancelled`  — the enrollment/session was cancelled (kept quiet in the UI).
// null         — no explicit status from the backend (treated as a plain seat).
export type LessonEnrollStatus = 'active' | 'cancelled' | 'waitlisted' | null

// Set only for past sessions the coach marked. null = not yet recorded. `excused` = absent
// with a good reason, which gave a session back (D3.13).
export type LessonAttendanceStatus = 'present' | 'absent' | 'excused' | null

export type LessonRow = {
  // A stable per-enrollee reference (the backend's enrollment key) — used as the
  // React list key.
  enrolleeRef: string
  enrolleeType: LessonEnrolleeType
  // "You" for the viewer's own seat, the child's name for a dependent.
  enrolleeName: string
  programId: string
  title: string
  sport: string
  orgId: string
  clubName: string
  // THE SEAT'S IDENTITY — the day the schedule PLANNED this session for, and the key every
  // package session and register mark is filed under. It is not necessarily the day the
  // family has to turn up: see `heldDate`.
  // Since D3.15 it is the session's KEY — the bare day for the class's first period, `<day>_p<n>`
  // for another — so it is passed back as is and never read as a day (`sessionKey.ts`).
  sessionDate: string
  // The planned day and the period it belongs to. Absent from an older Lambda, where
  // `sessionDate` is the day.
  day?: string
  periodId?: string
  // ── AS IT WILL ACTUALLY BE HELD (piece C2) ────────────────────────────────────
  // The club may move one session of a class by hand — a different day, time or court —
  // without moving the class. `heldDate` is the day to turn up on and `changed` is what
  // lets the row say where it came from; `startTime`/`endTime` are already the HELD ones.
  // Both are OPTIONAL so a site live ahead of the Lambda that sends them renders exactly
  // the screen it rendered yesterday, with the planned day, rather than a blank.
  // The server also splits `upcoming` / `past` on `heldDate`, so a class moved into next
  // week reaches the parent where they will see it.
  heldDate?: string
  changed?: boolean
  startTime: string // HH:MM
  endTime: string // HH:MM
  status: LessonEnrollStatus
  attendanceStatus: LessonAttendanceStatus
  // WHO cancelled, and whether anything came back. A `cancelled` row alone cannot
  // say: the club calling off a rained-out Tuesday and a family dropping out are the
  // same status, and only one of them returns the session. The server always sends
  // both, but they stay OPTIONAL here so the screen still renders against a Lambda
  // that predates them — an absent field degrades to the old neutral chip rather
  // than asserting something about the money.
  cancelledByClub?: boolean
  // The seat came out of a session package, so a session went back to it. The same
  // discriminator the cancellation email uses, so the two never promise differently.
  packaged?: boolean
  // What an excused absence gave back (D3.13) — `null` when nothing did (no purchase behind
  // the seat). Optional: a Lambda older than the field renders as before.
  excusedReturn?: ExcusedReturn | null
  // ── FOR THE ONE CANCEL CONFIRM (D4.14 follow-ups F2, §2.2) ──────────────────────
  // The child's id on a child's seat (the unenroll body's `dependentId`), null on the viewer's own;
  // what pays the seat (`seatPaidBy.ts#readSeatPaidBy` reads it); the club's notice window in hours
  // (0 = free until the start). Optional: an older Lambda sends none, and the confirm then reads
  // as a seat nothing pays, every cancel in time.
  dependentId?: string | null
  paidBy?: unknown
  cancelHours?: number | null
  // The club's IANA zone (backend 86e4ba6d): `heldDate` + `startTime` are its floating wall clock,
  // converted with it to judge "started" and "late". null when the club never set one.
  clubTimezone?: string | null
}

export type MyLessonsResponse = {
  upcoming: LessonRow[]
  past: LessonRow[]
}

export async function fetchMyLessons(): Promise<MyLessonsResponse> {
  return api.get<MyLessonsResponse>('/me/lessons', { handleAuthErrors: false })
}
